<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && customer"
        class="fixed inset-0 z-40 overflow-y-auto"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Header -->
            <div class="sticky top-0 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div class="flex items-center">
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mr-3">
                  <ViewIcon classes="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Customer Details
                </h3>
              </div>
              <button
                @click="$emit('close')"
                class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Content -->
            <div class="px-6 py-6">
              <!-- Account Status -->
              <div class="mb-4">
                <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 block">
                  Account Status
                </label>
                <span
                  :class="getStatusClass(customer.status)"
                  class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                >
                  <span
                    class="w-2 h-2 rounded-full mr-2"
                    :class="getStatusDotClass(customer.status)"
                  ></span>
                  {{ customer.status }}
                </span>
              </div>

              <!-- Category -->
              <div class="mb-6">
                <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 block">
                  Category
                </label>
                <span
                  class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                  :class="customer.category === 'internal'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400'
                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'"
                >
                  {{ customer.category === 'internal' ? 'Internal' : 'Customer' }}
                </span>
              </div>

              <!-- Personal Information -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                  Personal Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">First Name</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.first_name }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Last Name</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.last_name }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Email Address</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium break-all">{{ customer.email }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Mobile Number</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.userDetail?.mobile || customer.user_detail?.mobile || 'N/A' }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Brand</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.brand?.brand_name || 'N/A' }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Joined Date</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ formatDate(customer.created_at) }}</p>
                  </div>
                </div>
              </div>

              <!-- Address Information -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                  </svg>
                  Address Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">House No.</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.userDetail?.house_no || customer.user_detail?.house_no || 'N/A' }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Street</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.userDetail?.street || customer.user_detail?.street || 'N/A' }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Barangay</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.userDetail?.barangay || customer.user_detail?.barangay || 'N/A' }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">City</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ customer.userDetail?.city || customer.user_detail?.city || 'N/A' }}</p>
                  </div>
                </div>
              </div>

              <!-- Statistics -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Statistics
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Total Vehicles</label>
                    <p class="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{{ customer.vehicles?.length || 0 }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Total Tires</label>
                    <p class="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{{ customer.tires_count || 0 }}</p>
                  </div>
                </div>
              </div>

              <!-- Vehicles & Tires -->
              <div v-if="customer.vehicles && customer.vehicles.length > 0" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                  </svg>
                  Vehicles ({{ customer.vehicles.length }})
                </h3>

                <div class="space-y-4">
                  <!-- Vehicle Card -->
                  <div
                    v-for="vehicle in customer.vehicles"
                    :key="vehicle.vehicle_id"
                    class="border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <!-- Vehicle Header -->
                    <div
                      class="bg-gray-50 dark:bg-black px-4 py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
                      @click="toggleVehicle(vehicle.vehicle_id)"
                    >
                      <div class="flex items-center justify-between">
                        <div class="flex items-center space-x-3">
                          <!-- Vehicle Icon -->
                          <div
                            class="w-10 h-10 rounded-lg flex items-center justify-center"
                            :style="{ backgroundColor: 'var(--brand-primary)' + '15' }"
                          >
                            <svg class="w-6 h-6" :style="{ color: 'var(--brand-primary)' }" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                              <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                            </svg>
                          </div>

                          <!-- Vehicle Info -->
                          <div>
                            <h4 class="text-base font-semibold text-gray-900 dark:text-white">
                              {{ vehicle.vehicle_make }} {{ vehicle.vehicle_model }} {{ vehicle.vehicle_year }}
                            </h4>
                            <p class="text-sm text-gray-500 dark:text-gray-400">
                              Plate: {{ vehicle.plate_number }}
                            </p>
                          </div>
                        </div>

                        <div class="flex items-center space-x-3">
                          <!-- Tire Count Badge -->
                          <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                            :style="{
                              backgroundColor: 'var(--brand-accent)' + '20',
                              color: 'var(--brand-accent)'
                            }"
                          >
                            {{ vehicle.tires?.length || 0 }} {{ vehicle.tires?.length === 1 ? 'Tire Set' : 'Tire Sets' }}
                          </span>

                          <!-- Vehicle Photos Badge -->
                          <div class="relative group inline-block">
                            <button
                              v-if="vehicle.vehicle_photos && vehicle.vehicle_photos.length > 0"
                              @click.stop="openVehiclePhotoGallery(vehicle)"
                              class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 hover:shadow-md"
                              :style="{
                                backgroundColor: 'var(--brand-primary)' + '20',
                                color: 'var(--brand-primary)'
                              }"
                            >
                              <svg class="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                              </svg>
                              <span class="font-medium">{{ vehicle.vehicle_photos.length }}</span>
                              <svg class="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <span v-else class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-400">
                              <svg class="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                              </svg>
                              <span class="font-medium">0</span>
                            </span>

                            <!-- Tooltip -->
                            <div
                              v-if="vehicle.vehicle_photos && vehicle.vehicle_photos.length > 0"
                              class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 w-max text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999]"
                            >
                              Click to view {{ vehicle.vehicle_photos.length }} vehicle {{ vehicle.vehicle_photos.length === 1 ? 'photo' : 'photos' }}
                            </div>
                          </div>

                          <!-- Expand/Collapse Icon -->
                          <svg
                            class="w-5 h-5 text-gray-400 transition-transform duration-200"
                            :class="{ 'rotate-180': expandedVehicles.includes(vehicle.vehicle_id) }"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <!-- Tires List (Collapsible) -->
                    <div
                      v-if="expandedVehicles.includes(vehicle.vehicle_id)"
                      class="bg-white dark:bg-black px-4 py-3"
                    >
                      <!-- No Tires -->
                      <div
                        v-if="!vehicle.tires || vehicle.tires.length === 0"
                        class="text-center py-8 text-gray-500 dark:text-gray-400"
                      >
                        <svg class="w-12 h-12 mx-auto mb-3 opacity-50" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                        </svg>
                        <p class="text-sm">No tires registered for this vehicle</p>
                      </div>

                      <!-- Tires Grid -->
                      <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div
                          v-for="tire in vehicle.tires"
                          :key="tire.tire_id"
                          class="border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:shadow-md transition-shadow duration-200"
                        >
                          <!-- Tire Header -->
                          <div class="flex items-start justify-between mb-3">
                            <div class="flex items-center space-x-2">
                              <!-- Tire Icon -->
                              <div
                                class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                :style="{ backgroundColor: 'var(--brand-accent)' + '15' }"
                              >
                                <svg class="w-5 h-5" :style="{ color: 'var(--brand-accent)' }" fill="currentColor" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/>
                                  <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2" fill="none"/>
                                  <circle cx="12" cy="12" r="2" fill="currentColor"/>
                                </svg>
                              </div>
                              <div>
                                <h5 class="text-sm font-semibold text-gray-900 dark:text-white">
                                  {{ tire.brand?.brand_name || 'Unknown Brand' }}
                                </h5>
                                <p class="text-xs text-gray-500 dark:text-gray-400">{{ tire.pattern }}</p>
                              </div>
                            </div>

                            <!-- Approval Status Badge -->
                            <div>
                              <span
                                v-if="tire.approval_status === 'pending'"
                                class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                              >
                                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
                                </svg>
                                Pending
                              </span>
                              <span
                                v-else-if="tire.approval_status === 'approved'"
                                class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                              >
                                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                                </svg>
                                Approved
                              </span>
                              <span
                                v-else-if="tire.approval_status === 'rejected'"
                                class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                              >
                                <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
                                </svg>
                                Rejected
                              </span>
                            </div>
                          </div>

                          <!-- Tire Details -->
                          <div class="space-y-2 text-xs">
                            <div class="flex justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Size:</span>
                              <span class="font-medium text-gray-900 dark:text-white">{{ tire.tire_size }}</span>
                            </div>
                            <div class="flex justify-between">
                              <span class="text-gray-500 dark:text-gray-400">DOT:</span>
                              <span class="font-medium text-gray-900 dark:text-white">{{ tire.dot }}</span>
                            </div>
                            <div class="flex justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Invoice:</span>
                              <span class="font-medium text-gray-900 dark:text-white">{{ tire.invoice_number }}</span>
                            </div>
                            <div class="flex justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Shop:</span>
                              <span class="font-medium text-gray-900 dark:text-white">{{ tire.shop }}</span>
                            </div>
                            <div class="flex justify-between">
                              <span class="text-gray-500 dark:text-gray-400">Code:</span>
                              <span class="font-medium text-gray-900 dark:text-white">{{ tire.code }}</span>
                            </div>
                            <div class="flex justify-between items-center">
                              <span class="text-gray-500 dark:text-gray-400">Receipt Photos:</span>
                              <div class="relative group inline-block">
                                <button
                                  v-if="tire.receipt_photos && tire.receipt_photos.length > 0"
                                  @click="openPhotoGallery(tire)"
                                  class="inline-flex items-center px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                                  :style="{ color: 'var(--brand-accent)' }"
                                >
                                  <svg class="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                                  </svg>
                                  <span class="font-medium">{{ tire.receipt_photos.length }}</span>
                                  <svg class="w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                                <span v-else class="inline-flex items-center text-gray-400">
                                  <svg class="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                                  </svg>
                                  <span class="font-medium">0</span>
                                </span>

                                <!-- Tooltip (consistent with table action buttons) -->
                                <div
                                  v-if="tire.receipt_photos && tire.receipt_photos.length > 0"
                                  class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 w-max text-xs text-white bg-gray-900 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-[9999]"
                                >
                                  Click to view {{ tire.receipt_photos.length }} {{ tire.receipt_photos.length === 1 ? 'photo' : 'photos' }}
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Rejection Reason (if rejected) -->
                          <div v-if="tire.approval_status === 'rejected' && tire.rejection_reason" class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                            <p class="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Rejection Reason:</p>
                            <p class="text-xs text-gray-700 dark:text-gray-300">{{ tire.rejection_reason }}</p>
                          </div>

                          <!-- Inline Approval Actions (if pending) -->
                          <div v-if="tire.approval_status === 'pending'" class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 flex gap-2">
                            <button
                              @click="approveTireInline(tire)"
                              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors"
                            >
                              <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                              </svg>
                              Approve
                            </button>
                            <button
                              @click="openRejectModalInline(tire)"
                              class="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors"
                            >
                              <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                              </svg>
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Vehicles State -->
              <div v-else class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                  </svg>
                  Vehicles
                </h3>
                <div class="text-center py-12 bg-gray-50 dark:bg-black rounded-lg">
                  <svg class="w-16 h-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                  </svg>
                  <p class="text-gray-500 dark:text-gray-400">No vehicles registered yet</p>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="sticky bottom-0 bg-gray-50 dark:bg-black border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                @click="$emit('close')"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200 font-medium"
              >
                Close
              </button>
              <button
                @click="$emit('edit', customer)"
                class="px-4 py-2 text-white rounded-lg transition-colors duration-200 font-medium"
                style="background-color: var(--brand-primary);"
                @mouseover="(e) => (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--brand-hover)'"
                @mouseout="(e) => (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--brand-primary)'"
              >
                Edit Customer
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Photo Gallery Lightbox -->
    <Transition name="fade">
      <div
        v-if="photoGalleryOpen && selectedTire"
        class="fixed inset-0 z-[60] bg-black bg-opacity-95 flex items-center justify-center p-4"
        @click="closePhotoGallery"
      >
        <!-- Close Button -->
        <button
          @click="closePhotoGallery"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Previous Button -->
        <button
          v-if="currentPhotoIndex > 0"
          @click.stop="previousPhoto"
          class="absolute left-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <!-- Photo -->
        <div class="max-w-7xl max-h-full flex flex-col items-center" @click.stop>
          <!-- Tire Info Header -->
          <div class="bg-black bg-opacity-75 text-white px-6 py-3 rounded-t-lg mb-2 w-full">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">
                  {{ selectedTire.brand?.brand_name || 'Unknown Brand' }} - {{ selectedTire.pattern }}
                </h3>
                <p class="text-sm text-gray-300">
                  {{ selectedTire.tire_size }} | Invoice: {{ selectedTire.invoice_number }}
                </p>
              </div>
              <div class="text-sm text-gray-300">
                Photo {{ currentPhotoIndex + 1 }} of {{ selectedTire.receipt_photos?.length || 0 }}
              </div>
            </div>
          </div>

          <!-- Image -->
          <img
            v-if="selectedTire.receipt_photos && selectedTire.receipt_photos[currentPhotoIndex]"
            :src="getPhotoUrl(selectedTire.receipt_photos[currentPhotoIndex].photo_path)"
            :alt="`Receipt ${currentPhotoIndex + 1}`"
            class="max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl"
          />
        </div>

        <!-- Next Button -->
        <button
          v-if="selectedTire.receipt_photos && currentPhotoIndex < selectedTire.receipt_photos.length - 1"
          @click.stop="nextPhoto"
          class="absolute right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Thumbnail Strip -->
        <div
          v-if="selectedTire.receipt_photos && selectedTire.receipt_photos.length > 1"
          class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black bg-opacity-75 p-3 rounded-lg max-w-full overflow-x-auto"
          @click.stop
        >
          <button
            v-for="(photo, index) in selectedTire.receipt_photos"
            :key="photo.receipt_photo_id"
            @click="currentPhotoIndex = index"
            class="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all duration-200"
            :class="currentPhotoIndex === index ? 'border-white scale-110' : 'border-gray-600 opacity-60 hover:opacity-100'"
          >
            <img
              :src="getPhotoUrl(photo.photo_path)"
              :alt="`Thumbnail ${index + 1}`"
              class="w-full h-full object-cover"
            />
          </button>
        </div>
      </div>
    </Transition>

    <!-- Vehicle Photo Gallery Lightbox -->
    <Transition name="fade">
      <div
        v-if="vehiclePhotoGalleryOpen && selectedVehicle"
        class="fixed inset-0 z-[60] bg-black bg-opacity-95 flex items-center justify-center p-4"
        @click="closeVehiclePhotoGallery"
      >
        <!-- Close Button -->
        <button
          @click="closeVehiclePhotoGallery"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Previous Button -->
        <button
          v-if="currentVehiclePhotoIndex > 0"
          @click.stop="previousVehiclePhoto"
          class="absolute left-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <!-- Photo -->
        <div class="max-w-7xl max-h-full flex flex-col items-center" @click.stop>
          <!-- Vehicle Info Header -->
          <div class="bg-black bg-opacity-75 text-white px-6 py-3 rounded-t-lg mb-2 w-full">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">
                  {{ selectedVehicle.vehicle_make }} {{ selectedVehicle.vehicle_model }} {{ selectedVehicle.vehicle_year }}
                </h3>
                <p class="text-sm text-gray-300">
                  Plate: {{ selectedVehicle.plate_number }}
                </p>
              </div>
              <div class="text-sm text-gray-300">
                Photo {{ currentVehiclePhotoIndex + 1 }} of {{ selectedVehicle.vehicle_photos?.length || 0 }}
              </div>
            </div>
          </div>

          <!-- Image -->
          <img
            v-if="selectedVehicle.vehicle_photos && selectedVehicle.vehicle_photos[currentVehiclePhotoIndex]"
            :src="getPhotoUrl(selectedVehicle.vehicle_photos[currentVehiclePhotoIndex].photo_path)"
            :alt="`Vehicle Photo ${currentVehiclePhotoIndex + 1}`"
            class="max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl"
          />
        </div>

        <!-- Next Button -->
        <button
          v-if="selectedVehicle.vehicle_photos && currentVehiclePhotoIndex < selectedVehicle.vehicle_photos.length - 1"
          @click.stop="nextVehiclePhoto"
          class="absolute right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <!-- Thumbnail Strip -->
        <div
          v-if="selectedVehicle.vehicle_photos && selectedVehicle.vehicle_photos.length > 1"
          class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black bg-opacity-75 p-3 rounded-lg max-w-full overflow-x-auto"
          @click.stop
        >
          <button
            v-for="(photo, index) in selectedVehicle.vehicle_photos"
            :key="photo.vehicle_photo_id"
            @click="currentVehiclePhotoIndex = index"
            class="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all duration-200"
            :class="currentVehiclePhotoIndex === index ? 'border-white scale-110' : 'border-gray-600 opacity-60 hover:opacity-100'"
          >
            <img
              :src="getPhotoUrl(photo.photo_path)"
              :alt="`Thumbnail ${index + 1}`"
              class="w-full h-full object-cover"
            />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Tire Rejection Modal -->
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="showTireRejectModal"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
        @click="closeTireRejectModal"
      >
        <div
          class="bg-white dark:bg-black rounded-lg shadow-xl max-w-md w-full p-6"
          @click.stop
        >
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Reject Tire Registration</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Please provide a reason for rejecting this tire registration:
          </p>
          <textarea
            v-model="tireRejectionReason"
            rows="4"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-black text-gray-900 dark:text-white"
            placeholder="Enter rejection reason..."
          ></textarea>
          <div class="mt-6 flex gap-3 justify-end">
            <button
              @click="closeTireRejectModal"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-black rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="confirmTireReject"
              :disabled="!tireRejectionReason.trim()"
              class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reject Tire
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { Customer, Tire, Vehicle } from '@/types/customer';
import ViewIcon from '@/components/icons/ViewIcon.vue';
import { tireService } from '@/services/api/tireService';
import { useToast } from 'vue-toastification';

const toast = useToast();

defineProps<{
  isOpen: boolean;
  customer: Customer | null;
}>();

const emit = defineEmits<{
  close: [];
  edit: [customer: Customer];
  refresh: [];
}>();

// Track expanded vehicles
const expandedVehicles = ref<number[]>([]);

// Tire receipt photo gallery state
const photoGalleryOpen = ref(false);
const selectedTire = ref<Tire | null>(null);
const currentPhotoIndex = ref(0);

// Vehicle photo gallery state
const vehiclePhotoGalleryOpen = ref(false);
const selectedVehicle = ref<Vehicle | null>(null);
const currentVehiclePhotoIndex = ref(0);

// Tire rejection modal state
const showTireRejectModal = ref(false);
const selectedTireForReject = ref<Tire | null>(null);
const tireRejectionReason = ref('');

function toggleVehicle(vehicleId: number) {
  const index = expandedVehicles.value.indexOf(vehicleId);
  if (index > -1) {
    expandedVehicles.value.splice(index, 1);
  } else {
    expandedVehicles.value.push(vehicleId);
  }
}

function openPhotoGallery(tire: Tire) {
  selectedTire.value = tire;
  currentPhotoIndex.value = 0;
  photoGalleryOpen.value = true;
}

function closePhotoGallery() {
  photoGalleryOpen.value = false;
  selectedTire.value = null;
  currentPhotoIndex.value = 0;
}

function previousPhoto() {
  if (currentPhotoIndex.value > 0) {
    currentPhotoIndex.value--;
  }
}

function nextPhoto() {
  if (selectedTire.value?.receipt_photos && currentPhotoIndex.value < selectedTire.value.receipt_photos.length - 1) {
    currentPhotoIndex.value++;
  }
}

function openVehiclePhotoGallery(vehicle: Vehicle) {
  selectedVehicle.value = vehicle;
  currentVehiclePhotoIndex.value = 0;
  vehiclePhotoGalleryOpen.value = true;
}

function closeVehiclePhotoGallery() {
  vehiclePhotoGalleryOpen.value = false;
  selectedVehicle.value = null;
  currentVehiclePhotoIndex.value = 0;
}

function previousVehiclePhoto() {
  if (currentVehiclePhotoIndex.value > 0) {
    currentVehiclePhotoIndex.value--;
  }
}

function nextVehiclePhoto() {
  if (selectedVehicle.value?.vehicle_photos && currentVehiclePhotoIndex.value < selectedVehicle.value.vehicle_photos.length - 1) {
    currentVehiclePhotoIndex.value++;
  }
}

function getPhotoUrl(path: string): string {
  const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
  // const baseUrl = process.env.VUE_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
  return `${baseUrl}/storage/${path}`;
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-black/30 dark:text-gray-400';
  }
}

function getStatusDotClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500 dark:bg-yellow-400';
    case 'active':
      return 'bg-green-500 dark:bg-green-400';
    case 'rejected':
      return 'bg-red-500 dark:bg-red-400';
    default:
      return 'bg-gray-500 dark:bg-gray-400';
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Tire approval methods
async function approveTireInline(tire: Tire) {
  try {
    await tireService.approveTire(tire.tire_id);
    toast.success('Tire approved successfully');
    emit('refresh');
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to approve tire');
  }
}

function openRejectModalInline(tire: Tire) {
  selectedTireForReject.value = tire;
  tireRejectionReason.value = '';
  showTireRejectModal.value = true;
}

function closeTireRejectModal() {
  showTireRejectModal.value = false;
  selectedTireForReject.value = null;
  tireRejectionReason.value = '';
}

async function confirmTireReject() {
  if (!selectedTireForReject.value || !tireRejectionReason.value.trim()) return;

  try {
    await tireService.rejectTire(selectedTireForReject.value.tire_id, tireRejectionReason.value);
    toast.success('Tire rejected successfully');
    closeTireRejectModal();
    emit('refresh');
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to reject tire');
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.95);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

